import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactElement } from "react";
import { LandingPage } from "./pages/Landing";
import { getClinicianId, isCurrentUserAdmin } from "./lib/api";
import { RequireSpecialtyConfirmed } from "./components/RequireSpecialtyConfirmed";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";

// Code-splitting: solo se cargan si el cardiologo entra al area autenticada.
// La Landing queda eager porque es la primera vista publica de ecgwatch.online.
const LoginPage = lazy(() =>
  import("./pages/Login").then((m) => ({ default: m.LoginPage })),
);
const PatientsListPage = lazy(() =>
  import("./pages/PatientsList").then((m) => ({ default: m.PatientsListPage })),
);
const TriagePage = lazy(() =>
  import("./pages/Triage").then((m) => ({ default: m.TriagePage })),
);

const AdminFeedbackPage = lazy(() =>
  import("./pages/AdminFeedback").then((m) => ({ default: m.AdminFeedbackPage })),
);

const AdminTelemetryPage = lazy(() =>
  import("./pages/AdminTelemetry").then((m) => ({ default: m.AdminTelemetryPage })),
);

// PRD AdminPanel v0.2 — area /admin/* role-gated (S2.0+).
const AdminHomePage = lazy(() =>
  import("./pages/admin/AdminHome").then((m) => ({ default: m.AdminHomePage })),
);
const AdminOverviewPage = lazy(() =>
  import("./pages/admin/AdminOverview").then((m) => ({
    default: m.AdminOverviewPage,
  })),
);
const AdminCliniciansPage = lazy(() =>
  import("./pages/admin/AdminClinicians").then((m) => ({
    default: m.AdminCliniciansPage,
  })),
);
const AdminInvitationsPage = lazy(() =>
  import("./pages/admin/AdminInvitations").then((m) => ({
    default: m.AdminInvitationsPage,
  })),
);
const AdminAuditPage = lazy(() =>
  import("./pages/admin/AdminAudit").then((m) => ({
    default: m.AdminAuditPage,
  })),
);
const AdminSessionsPage = lazy(() =>
  import("./pages/admin/AdminSessions").then((m) => ({
    default: m.AdminSessionsPage,
  })),
);
const AdminAlgorithmsPage = lazy(() =>
  import("./pages/admin/AdminAlgorithms").then((m) => ({
    default: m.AdminAlgorithmsPage,
  })),
);
const AdminOrganizationsPage = lazy(() =>
  import("./pages/admin/AdminOrganizations").then((m) => ({
    default: m.AdminOrganizationsPage,
  })),
);
const AdminOrganizationDetailPage = lazy(() =>
  import("./pages/admin/AdminOrganizationDetail").then((m) => ({
    default: m.AdminOrganizationDetailPage,
  })),
);
const PatientDetailLegacyPage = lazy(() =>
  import("./pages/PatientDetail.legacy").then((m) => ({ default: m.PatientDetailPage })),
);
const PatientDetailV05Page = lazy(() =>
  import("./pages/PatientDetailV05").then((m) => ({
    default: m.PatientDetailV05Page,
  })),
);

// signed_report@v0.1 — M2 wizard + list + detail
const SignedReportsListPage = lazy(() =>
  import("./pages/SignedReportsList").then((m) => ({
    default: m.SignedReportsListPage,
  })),
);
const SignedReportWizardPage = lazy(() =>
  import("./pages/SignedReportWizard").then((m) => ({
    default: m.SignedReportWizardPage,
  })),
);
const SignedReportDetailPage = lazy(() =>
  import("./pages/SignedReportDetail").then((m) => ({
    default: m.SignedReportDetailPage,
  })),
);

const AdminPreviewDashboardV2Page = lazy(() =>
  import("./pages/AdminPreviewDashboardV2").then((m) => ({
    default: m.AdminPreviewDashboardV2Page,
  })),
);

const AccountSpecialtyPage = lazy(() =>
  import("./pages/AccountSpecialty").then((m) => ({ default: m.AccountSpecialtyPage })),
);

// Página pública /feedback — lazy para no inflar el bundle de la landing.
const FeedbackPage = lazy(() =>
  import("./pages/Feedback").then((m) => ({ default: m.FeedbackPage })),
);

// Página pública /methodology — lazy, no se carga hasta que alguien navegue ahí.
const MethodologyPage = lazy(() =>
  import("./pages/Methodology").then((m) => ({ default: m.MethodologyPage })),
);

const EquipoPage = lazy(() => import("./pages/Equipo"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const ConsentDocumentPage = lazy(() => import("./pages/ConsentDocument"));

// Página pública /aviso-regulatorio + /en/regulatory-notice — síntesis del
// Marco Operativo Pre-Comercial ECG.WATCH v1.0 (ECGW-MARCO-PRECOM-v1.0).
const RegulatoryNoticePage = lazy(() =>
  import("./components/RegulatoryNotice").then((m) => ({ default: m.RegulatoryNoticePage })),
);

// Página pública /demo — comparativa Apple Watch vs ECG.WATCH.
const DemoPage = lazy(() =>
  import("./pages/Demo").then((m) => ({ default: m.DemoPage })),
);

// Landing en inglés (/en) — lazy porque la mayoría del tráfico va a ES.
const LandingPageEn = lazy(() =>
  import("./pages/LandingEn").then((m) => ({ default: m.LandingPageEn })),
);

// Placeholder genérico para items "soon" de la sidebar.
const ComingSoonPage = lazy(() =>
  import("./pages/ComingSoon").then((m) => ({ default: m.ComingSoonPage })),
);

// Dev-only preview de ContinuousViewer. El lazy import ENTERO va detrás del
// guard import.meta.env.DEV — en builds de prod, Vite inlinea DEV como false
// y Rollup elimina el import() dinámico, evitando que el chunk se emita.
const ContinuousViewerPreviewPage = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/dev/ContinuousViewerPreview").then((m) => ({
        default: m.ContinuousViewerPreviewPage,
      })),
    )
  : null;

/** Fallback mientras carga un chunk. Minimal e invisible para evitar flash. */
function ChunkFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0e1a",
      }}
      aria-busy="true"
    />
  );
}

/** Wrapper que redirige a /login si no hay sesión local. */
function RequireAuth({ children }: { children: ReactElement }) {
  if (!getClinicianId()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/**
 * Wrapper que exige role='admin' en el clinician summary local.
 * Va anidado DENTRO de RequireAuth (asi el redirect a /login pega primero
 * si no hay sesion). Si hay sesion pero role no es admin, redirige a /triage.
 *
 * Lee de localStorage (getClinicianSummary), sin async — la pagina arranca
 * sin flicker. El BE igual valida con require_admin dependency, asi que
 * un usuario que modifique localStorage solo accede a la UI hasta que el
 * primer fetch devuelve 403.
 */
function RequireAdmin({ children }: { children: ReactElement }) {
  if (!isCurrentUserAdmin()) {
    return <Navigate to="/triage" replace />;
  }
  return children;
}

/** Wrapper que redirige al home si ya hay sesión local. */
function RedirectIfAuth({ children }: { children: ReactElement }) {
  if (getClinicianId()) {
    return <Navigate to="/triage" replace />;
  }
  return children;
}

export function App() {
  return (
    // RouteErrorBoundary FUERA del Suspense: Suspense no atrapa errores. Si un
    // chunk lazy rejecta (hash viejo post-deploy / red), el error sube al
    // boundary → recarga (chunk) o fallback (otro). Sin él: pantalla blanca.
    <RouteErrorBoundary>
      <Suspense fallback={<ChunkFallback />}>
        <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuth>
            <LoginPage />
          </RedirectIfAuth>
        }
      />
      <Route path="/" element={<LandingPage />} />
      <Route path="/en" element={<LandingPageEn />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route
        path="/console/account/specialty"
        element={
          <RequireAuth>
            <AccountSpecialtyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/triage"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <TriagePage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/pacientes"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <PatientsListPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/pacientes/v05/:patientId"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <PatientDetailV05Page />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/pacientes/legacy/:patientId"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <PatientDetailLegacyPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/pacientes/:patientId"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <PatientDetailV05Page />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/informes"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <SignedReportsListPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/informes/nuevo"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <SignedReportWizardPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/informes/:reportId/editar"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <SignedReportWizardPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route
        path="/informes/:reportId"
        element={
          <RequireAuth>
            <RequireSpecialtyConfirmed>
              <SignedReportDetailPage />
            </RequireSpecialtyConfirmed>
          </RequireAuth>
        }
      />
      <Route path="/methodology" element={<MethodologyPage />} />
      <Route path="/equipo" element={<EquipoPage />} />
      <Route path="/privacidad" element={<PrivacyPolicyPage />} />
      <Route path="/consentimiento" element={<ConsentDocumentPage />} />
      <Route path="/aviso-regulatorio" element={<RegulatoryNoticePage lang="es" />} />
      <Route path="/en/regulatory-notice" element={<RegulatoryNoticePage lang="en" />} />
      <Route path="/comparativa" element={<DemoPage />} />
      <Route
        path="/admin/feedback"
        element={
          <RequireAuth>
            <AdminFeedbackPage />
          </RequireAuth>
        }
      />

      <Route
        path="/admin/preview/dashboard-v2/:patientId?"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminPreviewDashboardV2Page />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/console/coming-soon/:slug"
        element={
          <RequireAuth>
            <ComingSoonPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminHomePage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/overview"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminOverviewPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/clinicians"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminCliniciansPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/invitations"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminInvitationsPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminAuditPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/sessions"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminSessionsPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/algorithms"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminAlgorithmsPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminOrganizationsPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/organizations/:id"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminOrganizationDetailPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      {import.meta.env.DEV && ContinuousViewerPreviewPage && (
        <Route
          path="/dev/continuous-viewer"
          element={<ContinuousViewerPreviewPage />}
        />
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
            <Route
        path="/admin/telemetry"
        element={
          <RequireAuth>
            <AdminTelemetryPage />
          </RequireAuth>
        }
      />

      </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}
